/**
 * 로그인 페이지 배경용 기계 요소 아이콘 컴포넌트들
 * 색상은 CSS 변수 또는 props로 커스터마이징 가능
 */

import type { CSSProperties } from "react";

// 기본 색상 (CSS 변수 fallback)
const DEFAULT_BG = "var(--color-bg, #F8FAFC)";
const DEFAULT_ACCENT = "var(--color-accent, #0EA5E9)";

interface IconProps {
  size: number;
  className?: string;
  style?: CSSProperties;
  /** 배경색 (기본: CSS 변수 --color-bg) */
  bgColor?: string;
  /** 액센트색 (기본: CSS 변수 --color-accent) */
  accentColor?: string;
}

/**
 * 정밀 기어 컴포넌트 - 톱니바퀴
 */
export function PrecisionGear({
  size,
  teeth = 12,
  className,
  style,
  bgColor = DEFAULT_BG,
}: IconProps & { teeth?: number }) {
  const innerRadius = size * 0.25;
  const outerRadius = size * 0.42;
  const toothHeight = size * 0.08;

  const toothPath = Array.from({ length: teeth }, (_, i) => {
    const angle = (i / teeth) * Math.PI * 2;
    const nextAngle = ((i + 0.5) / teeth) * Math.PI * 2;
    const midAngle = ((i + 0.25) / teeth) * Math.PI * 2;
    const midAngle2 = ((i + 0.75) / teeth) * Math.PI * 2;

    const cx = size / 2;
    const cy = size / 2;

    const x1 = cx + Math.cos(angle) * outerRadius;
    const y1 = cy + Math.sin(angle) * outerRadius;
    const x2 = cx + Math.cos(midAngle) * (outerRadius + toothHeight);
    const y2 = cy + Math.sin(midAngle) * (outerRadius + toothHeight);
    const x3 = cx + Math.cos(nextAngle) * (outerRadius + toothHeight);
    const y3 = cy + Math.sin(nextAngle) * (outerRadius + toothHeight);
    const x4 = cx + Math.cos(midAngle2) * outerRadius;
    const y4 = cy + Math.sin(midAngle2) * outerRadius;

    return `L ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3} L ${x4} ${y4}`;
  }).join(" ");

  const startX = size / 2 + outerRadius;
  const startY = size / 2;

  return (
    <div className={className} style={{ width: size, height: size, ...style }}>
      <svg viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d={`M ${startX} ${startY} ${toothPath} Z`} fill="currentColor" />
        <circle cx={size / 2} cy={size / 2} r={innerRadius * 1.2} fill={bgColor} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={innerRadius * 0.8}
          fill="none"
          stroke="currentColor"
          strokeWidth={size * 0.04}
        />
        <circle cx={size / 2} cy={size / 2} r={size * 0.06} fill="currentColor" />
        {[0, 60, 120, 180, 240, 300].map((angle) => (
          <line
            key={angle}
            x1={size / 2}
            y1={size / 2}
            x2={size / 2 + Math.cos((angle * Math.PI) / 180) * innerRadius * 0.6}
            y2={size / 2 + Math.sin((angle * Math.PI) / 180) * innerRadius * 0.6}
            stroke="currentColor"
            strokeWidth={size * 0.02}
          />
        ))}
      </svg>
    </div>
  );
}

/**
 * 압력 게이지 컴포넌트
 */
export function PressureGauge({
  size,
  className,
  style,
  needleAngle = -30,
  bgColor = DEFAULT_BG,
  accentColor = DEFAULT_ACCENT,
}: IconProps & { needleAngle?: number }) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.4;

  return (
    <div className={className} style={{ width: size, height: size, ...style }}>
      <svg viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill={bgColor}
          stroke="currentColor"
          strokeWidth={size * 0.05}
        />
        <circle
          cx={cx}
          cy={cy}
          r={radius * 0.85}
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
          opacity={0.3}
        />
        {Array.from({ length: 11 }, (_, i) => {
          const angle = -135 + i * 27;
          const rad = (angle * Math.PI) / 180;
          const innerR = radius * 0.7;
          const outerR = radius * 0.85;
          return (
            <line
              key={i}
              x1={cx + Math.cos(rad) * innerR}
              y1={cy + Math.sin(rad) * innerR}
              x2={cx + Math.cos(rad) * outerR}
              y2={cy + Math.sin(rad) * outerR}
              stroke="currentColor"
              strokeWidth={i % 5 === 0 ? 2 : 1}
              opacity={i % 5 === 0 ? 0.8 : 0.4}
            />
          );
        })}
        <g style={{ transformOrigin: `${cx}px ${cy}px`, transform: `rotate(${needleAngle}deg)` }}>
          <path
            d={`M ${cx - 3} ${cy} L ${cx} ${cy - radius * 0.65} L ${cx + 3} ${cy} Z`}
            fill={accentColor}
          />
        </g>
        <circle cx={cx} cy={cy} r={size * 0.06} fill="currentColor" />
      </svg>
    </div>
  );
}

/**
 * 육각 볼트 컴포넌트
 */
export function HexBolt({ size, className, style, bgColor = DEFAULT_BG }: IconProps) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.4;

  const hexPoints = Array.from({ length: 6 }, (_, i) => {
    const angle = (i / 6) * Math.PI * 2 - Math.PI / 6;
    return `${cx + Math.cos(angle) * r},${cy + Math.sin(angle) * r}`;
  }).join(" ");

  return (
    <div className={className} style={{ width: size, height: size, ...style }}>
      <svg viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
        <polygon points={hexPoints} fill="currentColor" />
        <polygon
          points={Array.from({ length: 6 }, (_, i) => {
            const angle = (i / 6) * Math.PI * 2 - Math.PI / 6;
            return `${cx + Math.cos(angle) * r * 0.45},${cy + Math.sin(angle) * r * 0.45}`;
          }).join(" ")}
          fill={bgColor}
        />
      </svg>
    </div>
  );
}

/**
 * 측정 스케일 컴포넌트
 */
export function MeasureScale({
  width,
  height,
  className,
  style,
}: {
  width: number;
  height: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div className={className} style={{ width, height, ...style }}>
      <svg viewBox={`0 0 ${width} ${height}`} fill="none" xmlns="http://www.w3.org/2000/svg">
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="currentColor"
          strokeWidth={1}
        />
        {Array.from({ length: Math.floor(width / 8) + 1 }, (_, i) => {
          const x = i * 8;
          const tickHeight = i % 5 === 0 ? height * 0.4 : height * 0.2;
          return (
            <line
              key={i}
              x1={x}
              y1={height / 2 - tickHeight / 2}
              x2={x}
              y2={height / 2 + tickHeight / 2}
              stroke="currentColor"
              strokeWidth={i % 5 === 0 ? 1.5 : 0.5}
              opacity={i % 5 === 0 ? 0.6 : 0.3}
            />
          );
        })}
      </svg>
    </div>
  );
}

/**
 * 렌치 컴포넌트
 */
export function Wrench({ size, className, style, bgColor = DEFAULT_BG }: IconProps) {
  return (
    <div className={className} style={{ width: size, height: size, ...style }}>
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M25 20 L35 10 L50 10 L50 25 L40 35 L25 35 Z" fill="currentColor" opacity={0.8} />
        <path d="M30 18 L38 18 L38 27 L30 27 Z" fill={bgColor} />
        <rect x="35" y="30" width="12" height="55" rx="3" fill="currentColor" opacity={0.6} />
        <line x1="41" y1="40" x2="41" y2="75" stroke={bgColor} strokeWidth="2" opacity={0.5} />
      </svg>
    </div>
  );
}

/**
 * 다이얼 노브 컴포넌트
 */
export function DialKnob({
  size,
  className,
  style,
  rotation = 0,
  bgColor = DEFAULT_BG,
}: IconProps & { rotation?: number }) {
  return (
    <div className={className} style={{ width: size, height: size, ...style }}>
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle
          cx="50"
          cy="50"
          r="42"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          opacity={0.3}
        />
        {Array.from({ length: 12 }, (_, i) => {
          const angle = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const r1 = 35;
          const r2 = 42;
          return (
            <line
              key={i}
              x1={50 + Math.cos(angle) * r1}
              y1={50 + Math.sin(angle) * r1}
              x2={50 + Math.cos(angle) * r2}
              y2={50 + Math.sin(angle) * r2}
              stroke="currentColor"
              strokeWidth={i % 3 === 0 ? 2 : 1}
              opacity={i % 3 === 0 ? 0.6 : 0.3}
            />
          );
        })}
        <circle cx="50" cy="50" r="28" fill="currentColor" opacity={0.8} />
        <circle cx="50" cy="50" r="22" fill={bgColor} />
        <circle cx="50" cy="50" r="18" fill="currentColor" opacity={0.15} />
        <g style={{ transformOrigin: "50px 50px", transform: `rotate(${rotation}deg)` }}>
          <line
            x1="50"
            y1="50"
            x2="50"
            y2="28"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx="50" cy="30" r="4" fill="currentColor" />
        </g>
        <circle cx="50" cy="50" r="5" fill="currentColor" />
      </svg>
    </div>
  );
}
